import React from 'react';
import { TrendingDown, Target, Check } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from 'recharts';

interface InflationPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const InflationPanel: React.FC<InflationPanelProps> = ({ isMinimized, onMinimize }) => {
  const { inflation } = macroDetailData;
  const cpiChange = inflation.cpiCurrent - inflation.cpiPrev;

  return (
    <Panel title="INFLATION & PRICES" subtitle="CPI" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Section - Current vs Target */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Current CPI */}
          <div className="text-center py-5 px-4 bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg border border-data-amber/20">
            <div className="text-xs text-data-amber uppercase tracking-widest font-semibold mb-2">
              Current CPI
            </div>
            <div className="text-5xl font-mono font-black text-white mb-1">
              {inflation.cpiCurrent}
              <span className="text-2xl">%</span>
            </div>
            <div className="flex items-center justify-center gap-1 text-data-green">
              <TrendingDown size={14} />
              <span className="text-sm font-semibold">{cpiChange.toFixed(1)}% MoM</span>
            </div>
          </div>

          {/* Target */}
          <div className="text-center py-5 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20">
            <div className="text-xs text-data-green uppercase tracking-widest font-semibold mb-2">
              Target
            </div>
            <div className="text-5xl font-mono font-black text-data-green mb-1">
              {inflation.cpiTarget}
              <span className="text-2xl">%</span>
            </div>
            <div className="flex items-center justify-center gap-1">
              <Target size={14} className="text-gray-500" />
              <span className="text-sm text-gray-500">by 2026</span>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 mb-4">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Breakdown</div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-mono font-bold text-white">{inflation.coreInflation}%</div>
              <div className="text-xs text-gray-500">Core</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-mono font-bold text-data-amber">{inflation.foodInflation}%</div>
              <div className="text-xs text-gray-500">Food</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-mono font-bold text-data-red">{inflation.ppiCurrent}%</div>
              <div className="text-xs text-gray-500">PPI</div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 mb-4 flex-1 min-h-[120px]">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">6-Month Trend</div>
          <ResponsiveContainer width="100%" height={80}>
            <LineChart data={inflation.history} margin={{ top: 5, right: 10, bottom: 0, left: 10 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide domain={[4, 12]} />
              <ReferenceLine y={5} stroke="#ffb800" strokeDasharray="3 3" strokeWidth={1} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                formatter={(value: number) => [`${value}%`, 'CPI']}
              />
              <Line
                type="monotone"
                dataKey="cpi"
                stroke="#00d26a"
                strokeWidth={2}
                dot={{ fill: '#00d26a', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-xs mt-2">
            <span className="text-data-green flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-data-green" /> Actual
            </span>
            <span className="text-data-amber flex items-center gap-1">
              <span className="w-4 border-t border-dashed border-data-amber" /> Target
            </span>
          </div>
        </div>

        {/* Forecast */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Forecast</div>
          <div className="grid grid-cols-4 gap-2">
            {inflation.forecast.map((f) => (
              <div key={f.year} className="text-center p-2 bg-terminal-border/30 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">{f.year}</div>
                <div className={`text-lg font-mono font-bold flex items-center justify-center gap-1 ${
                  f.value <= 5 ? 'text-data-green' : 'text-data-amber'
                }`}>
                  {f.value}%
                  {f.value <= 5 && <Check size={12} />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.CBU} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default InflationPanel;
